import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  convertToRestDay,
  reloadAfterCacheUpdate,
} from '../models/convertToRestDay';

export type ConvertToRestDayOutcome = {
  workoutId: number;
  title: string;
  success: boolean;
};

// What the page renders: the confirmation, and the last result to announce.
export type ConvertToRestDayVM = {
  isOpen: boolean;
  // The workout waiting for confirmation, empty while none is.
  workoutTitle: string;
  confirm: () => void;
  cancel: () => void;
  outcome: ConvertToRestDayOutcome | null;
  clearOutcome: () => void;
};

type PendingConversion = { workoutId: number; title: string };

// The confirm, convert and refresh flow shared by the legacy workouts tables.
// `openConfirmation` opens the modal, `view` is what the page renders, and
// `convertingWorkoutIds` are the rows to show as loading while the change is
// saved. Lists show the result from the cache at once, then reload quietly.
export const useConvertToRestDay = (onConverted: () => unknown) => {
  const { cache } = useApolloClient();
  const [pending, setPending] = useState<PendingConversion | null>(null);
  const [outcome, setOutcome] = useState<ConvertToRestDayOutcome | null>(null);
  const [converting, setConverting] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const setConvertingOne = (workoutId: number, isConverting: boolean) =>
    setConverting((prev) => {
      const next = new Set(prev);
      if (isConverting) next.add(workoutId);
      else next.delete(workoutId);
      return next;
    });

  const confirm = () => {
    if (!pending) return;
    const { workoutId, title } = pending;
    setPending(null);
    setConvertingOne(workoutId, true);
    convertToRestDay(workoutId, cache)
      .then(() => {
        setOutcome({ workoutId, title, success: true });
        reloadAfterCacheUpdate(onConverted);
      })
      .catch(() => setOutcome({ workoutId, title, success: false }))
      .finally(() => setConvertingOne(workoutId, false));
  };

  const view: ConvertToRestDayVM = {
    isOpen: pending !== null,
    workoutTitle: pending?.title ?? '',
    confirm,
    cancel: () => setPending(null),
    outcome,
    clearOutcome: () => setOutcome(null),
  };

  return {
    openConfirmation: (workoutId: number, title: string) =>
      setPending({ workoutId, title }),
    convertingWorkoutIds: converting,
    view,
  };
};
